import React, {
  useState,
  useContext,
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from 'react'

type AudienceAppStateContextValue = {
  liveId: string
  liveToken: string
  error: Error | null
  setError: Dispatch<SetStateAction<Error | null>>
  scope: string | null
  setScope: Dispatch<SetStateAction<string | null>>
  publicChannelURL: string | null
  setPublicChannelURL: Dispatch<SetStateAction<string | null>>
}

type AudienceAppStateProviderProps = PropsWithChildren<{
  liveId: string
  liveToken: string
}>

const AudienceAppStateContext =
  React.createContext<AudienceAppStateContextValue | null>(null)

export const AudienceAppStateProvider = ({
  liveId,
  liveToken,
  children,
}: AudienceAppStateProviderProps) => {
  const [error, setError] = useState<Error | null>(null)
  const [scope, setScope] = useState<string | null>(null)
  const [publicChannelURL, setPublicChannelURL] = useState<string | null>(null)

  const value = useMemo(
    () => ({
      liveId,
      liveToken,
      error,
      setError,
      scope,
      setScope,
      publicChannelURL,
      setPublicChannelURL,
    }),
    [error, liveId, liveToken, publicChannelURL, scope]
  )

  return (
    <AudienceAppStateContext.Provider value={value}>
      {children}
    </AudienceAppStateContext.Provider>
  )
}

export const useAudienceAppState = () => {
  const context = useContext(AudienceAppStateContext)

  if (context === null) {
    throw new Error(
      'useAudienceAppState must be used within an AudienceAppStateProvider.'
    )
  }

  return context
}
